'use client';

import * as React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HubSpotFormModal } from '@/components/hubspot/HubSpotFormModal';
import { CTA } from '@/types/sanity';

interface CTAButtonProps {
  cta: CTA;
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'xs' | 'icon-sm' | 'icon-lg' | 'icon-xs';
  className?: string;
  modalTitle?: string;
  modalDescription?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
}

export function CTAButton({
  cta,
  size = 'lg',
  className,
  modalTitle,
  modalDescription,
  variant: variantOverride,
}: CTAButtonProps) {
  const [modalOpen, setModalOpen] = React.useState(false);

  const variant =
    variantOverride ||
    (cta.variant === 'outline' ? 'outline' : cta.variant === 'secondary' ? 'secondary' : 'default');

  const isPrimary = variant === 'default';
  const arrow = isPrimary ? <ArrowRight className="ml-0.5 h-4 w-4" /> : null;

  // Scroll action type — smooth-scrolls to the next section below the banner
  if (cta.actionType === 'scroll') {
    const handleScroll = (e: React.MouseEvent<HTMLButtonElement>) => {
      const section = (e.target as HTMLElement).closest('section');
      const nextSection = section?.nextElementSibling as HTMLElement | null;
      if (nextSection) {
        nextSection.scrollIntoView({ behavior: 'smooth' });
      }
    };

    return (
      <Button variant={variant} size={size} className={className} onClick={handleScroll}>
        {cta.label}
        {arrow}
      </Button>
    );
  }

  // Modal action type
  if (cta.actionType === 'modal' && cta.hubspotFormId) {
    return (
      <>
        <Button
          variant={variant}
          size={size}
          className={className}
          onClick={() => setModalOpen(true)}
        >
          {cta.label}
          {arrow}
        </Button>
        <HubSpotFormModal
          formId={cta.hubspotFormId}
          open={modalOpen}
          onOpenChange={setModalOpen}
          title={modalTitle || cta.label}
          description={modalDescription}
        />
      </>
    );
  }

  // Link or external action type
  const rawHref = cta.actionType === 'external' ? cta.externalLink : cta.internalLink;
  const href =
    cta.actionType === 'external' && rawHref && !/^https?:\/\//.test(rawHref)
      ? '#'
      : rawHref || '#';

  return (
    <Button variant={variant} size={size} className={className} asChild>
      <Link
        href={href}
        target={cta.actionType === 'external' ? '_blank' : undefined}
        rel={cta.actionType === 'external' ? 'noopener noreferrer' : undefined}
      >
        {cta.label}
        {arrow}
      </Link>
    </Button>
  );
}
